import { linearSumAssignment } from 'linear-sum-assignment';
import { Matrix } from 'ml-matrix';
import { gaussianFct } from 'ml-peak-shape-generator';
import { xFindClosestIndex, xMaxValue } from 'ml-spectra-processing';
import { addDummySignals } from '../signals/addDummySignals';
import { signalsToSpinSystem } from '../signals/simulation/signalsToSpinSystem';
import { simulateXYPeaks } from '../signals/simulation/simulateXYPeaks';
import { splitSpinSystem } from '../signals/simulation/splitSpinSystem';
export function solventSuppression(peakList, solvent, options = {}) {
    const peaks = [...peakList].sort((a, b) => a.x - b.x);
    const xValues = peaks.map((peak) => peak.x);
    const { markSolventPeaks = false, solventZoneExtension = 1.2 } = options;
    for (const solventSignal of solvent) {
        let solventXYPeaks = solventSignal.peaks
            ? solventSignal.peaks
            : getSolventPeaks(solventSignal);
        solventXYPeaks.sort((a, b) => a.x - b.x);
        let upIndex = xFindClosestIndex(xValues, solventXYPeaks[solventXYPeaks.length - 1].x + solventZoneExtension);
        let lowIndex = xFindClosestIndex(xValues, solventXYPeaks[0].x - solventZoneExtension);
        if (upIndex === lowIndex)
            continue;
        const nearPeaks = peaks.slice(lowIndex, upIndex + 1);
        const amplitudeResiduals = [];
        const deltaResiduals = [];
        const positionResiduals = [];
        for (let peak of nearPeaks) {
            const { peaks: shiftedSolventPeaks, delta: currentDelta } = getShiftedSolventPeaks(peak, solventSignal, solventXYPeaks);
            const closestPeaks = getClosestPeaks(shiftedSolventPeaks, nearPeaks);
            let deltaResidual = 0;
            let amplitudeResidual = 0;
            let positionResidual = 0;
            for (let i = 0; i < closestPeaks.length; i++) {
                amplitudeResidual += Math.abs(shiftedSolventPeaks[i].y - closestPeaks[i].y);
                deltaResidual += Math.abs(shiftedSolventPeaks[i].x - closestPeaks[i].x);
            }
            if (closestPeaks.length === 0) {
                deltaResidual = Number.MAX_SAFE_INTEGER;
                amplitudeResidual = Number.MAX_SAFE_INTEGER;
                positionResidual = Number.MAX_SAFE_INTEGER;
            }
            else {
                positionResidual = gaussianFct(Math.abs(solventSignal.delta - currentDelta), 0.5);
            }
            amplitudeResiduals.push(amplitudeResidual);
            deltaResiduals.push(deltaResidual);
            positionResiduals.push(positionResidual);
        }
        const maxAmplitude = xMaxValue(amplitudeResiduals);
        const maxDelta = xMaxValue(deltaResiduals);
        const maxPosition = xMaxValue(positionResiduals);
        let minIndex = -1;
        let minScore = Number.MAX_SAFE_INTEGER;
        let score = [];
        for (let i = 0; i < deltaResiduals.length; i++) {
            const value = (amplitudeResiduals[i] / maxAmplitude +
                deltaResiduals[i] / maxDelta +
                1 -
                positionResiduals[i] / maxPosition) /
                3;
            score.push(value);
            if (minScore > value) {
                minIndex = i;
                minScore = value;
            }
        }
        if (minScore < 0) {
            new Error('There is not a correct match with the pattern');
            return peaks;
        }
        const { peaks: shiftedSolventPeaks } = getShiftedSolventPeaks(nearPeaks[minIndex], solventSignal, solventXYPeaks);
        const diff = getDiffMatrix(shiftedSolventPeaks, nearPeaks);
        const { rowAssignments, gain } = linearSumAssignment(diff, {
            maximaze: false,
        });
        if (gain < 0) {
            new Error('The gain is below to zero');
            return peaks;
        }
        if (markSolventPeaks) {
            for (let index of rowAssignments) {
                peaks[index + lowIndex].kind = 'solvent';
            }
        }
        else {
            rowAssignments.sort((a, b) => b - a);
            for (let index of rowAssignments) {
                peaks.splice(index + lowIndex, 1);
            }
        }
    }
    return peaks;
}
function getSolventPeaks(signal, options = {}) {
    let signals = addDummySignals([signal]);
    let spinSystem = signalsToSpinSystem(signals);
    const { frequency = 400, maxClusterSize = 8 } = options;
    spinSystem.clusters = splitSpinSystem(spinSystem, {
        frequency,
        maxClusterSize,
    });
    const peaks = simulateXYPeaks(spinSystem);
    return peaks.filter((peak) => peak.x < 1000);
}
function getShiftedSolventPeaks(peak, solventSignal, solventXYPeaks) {
    const shiftedSolventPeaks = JSON.parse(JSON.stringify(solventXYPeaks));
    // shift x values of solventPeaks to center it to the current peak.
    let deltaPPM = peak.x - solventSignal.delta;
    if (solventXYPeaks.length > 1 && solventXYPeaks.length % 2 === 0) {
        deltaPPM += solventXYPeaks[0].x;
    }
    const maxIntensity = shiftedSolventPeaks.reduce((max, current) => (current.y > max ? current.y : max), shiftedSolventPeaks[0].y);
    let currentDelta = 0;
    for (let shiftedSolventPeak of shiftedSolventPeaks) {
        shiftedSolventPeak.x += deltaPPM;
        shiftedSolventPeak.y /= maxIntensity;
        currentDelta += shiftedSolventPeak.x;
    }
    return {
        peaks: shiftedSolventPeaks,
        delta: currentDelta / shiftedSolventPeaks.length,
    };
}
function getClosestPeaks(shiftedSolventPeaks, nearPeaks) {
    const diff = getDiffMatrix(shiftedSolventPeaks, nearPeaks);
    const { rowAssignments, gain } = linearSumAssignment(diff, {
        maximaze: false,
    });
    if (gain === -1)
        return [];
    const assignmentPeaks = [];
    let maxValue = Number.MIN_SAFE_INTEGER;
    for (let index of rowAssignments) {
        if (maxValue < nearPeaks[index].y)
            maxValue = nearPeaks[index].y;
        assignmentPeaks.push({ ...nearPeaks[index] });
    }
    assignmentPeaks.forEach((peak, i, arr) => (arr[i].y /= maxValue));
    return assignmentPeaks;
}
function getDiffMatrix(rows, columns) {
    const nbColumns = columns.length;
    const nbRows = rows.length;
    const diff = new Matrix(nbRows, nbColumns);
    for (let r = 0; r < nbRows; r++) {
        for (let c = 0; c < nbColumns; c++) {
            diff.set(r, c, Math.abs(rows[r].x - columns[c].x));
        }
    }
    return diff;
}
//# sourceMappingURL=solventSuppression.js.map